// src/store/organization-store.ts
import { create } from 'zustand';
import { toast } from 'sonner';
import * as api from '../api/page';
import type { 
    Organization,
    OrganizationStats,
    OrganizationMember,
    OrganizationInvitation,
    UpdateOrganizationPayload,
    UserPermissions
} from '../types/index';

interface OrganizationState {
    organization: Organization | null;
    organizationStats: OrganizationStats | null;
    members: OrganizationMember[];
    invitations: OrganizationInvitation[];
    userPermissions: UserPermissions | null;
    isOrgLoading: boolean;
    isMembersLoading: boolean;
    isInvitationsLoading: boolean;
    isPermissionsLoading: boolean;
    isUpdating: boolean;
    orgPromise: Promise<void> | null;
    permissionsPromise: Promise<void> | null;
    membersPromise: Promise<void> | null;
    invitationsPromise: Promise<void> | null;
    fetchOrganization: () => Promise<void>;
    updateOrganization: (data: UpdateOrganizationPayload) => Promise<boolean>;
    fetchMembers: () => Promise<void>;
    fetchInvitations: () => Promise<void>;
    removeMember: (member: OrganizationMember) => Promise<void>;
    banMember: (member: OrganizationMember, reason?: string) => Promise<void>;
    unbanMember: (member: OrganizationMember) => Promise<void>;
    refreshInvitations: () => Promise<void>;
    fetchUserPermissions: () => Promise<void>;
    clearOrganization: () => void;
    // --- ADDED LOGO ACTIONS ---
    uploadLogo: (file: File) => Promise<void>;
    deleteLogo: () => Promise<void>;
    // --------------------------
}

export const useOrganizationStore = create<OrganizationState>((set, get) => ({
    // --- INITIAL STATE ---
    organization: null,
    organizationStats: null,
    members: [],
    invitations: [],
    userPermissions: null,
    isOrgLoading: false,
    isMembersLoading: false,
    isInvitationsLoading: false,
    isPermissionsLoading: true,
    isUpdating: false,
    orgPromise: null,
    permissionsPromise: null,
    membersPromise: null,
    invitationsPromise: null,

    // --- ACTIONS ---

    fetchOrganization: async () => {
        if (get().orgPromise) await get().orgPromise;
        const promise = (async () => {
            set({ isOrgLoading: true });
            try {
                const [orgData, statsData] = await Promise.all([
                    api.getMyOrganization(),
                    api.getOrganizationStats(),
                ]);
                // Organization model now includes company_logo_url
                set({ organization: orgData, organizationStats: statsData });
            } catch (error: any) {
                // Non-critical if it fails, might just not be in an org
            } finally {
                set({ isOrgLoading: false, orgPromise: null });
            }
        })();
        set({ orgPromise: promise });
        await promise;
    },
    
    fetchMembers: async () => {
        if (get().membersPromise) await get().membersPromise;
        const promise = (async () => {
            set({ isMembersLoading: true });
            try {
                const membersData = await api.getOrganizationMembers();
                set({ members: membersData });
            } catch (error: any) {
                toast.error("Failed to load team members", { description: error.message });
            } finally {
                set({ isMembersLoading: false, membersPromise: null });
            }
        })();
        set({ membersPromise: promise });
        await promise;
    },

    fetchInvitations: async () => {
        if (get().invitationsPromise) await get().invitationsPromise;
        const promise = (async () => {
            set({ isInvitationsLoading: true });
            try {
                const invitationsData = await api.getInvitations();
                set({ invitations: invitationsData });
            } catch (error: any) {
                toast.error("Failed to load invitations", { description: error.message });
            } finally {
                set({ isInvitationsLoading: false, invitationsPromise: null });
            }
        })();
        set({ invitationsPromise: promise });
        await promise;
    },
    
    refreshInvitations: async () => {
        set({ isInvitationsLoading: true, invitationsPromise: null });
        try {
            const invitationsData = await api.getInvitations();
            set({ invitations: invitationsData });
        } catch (error: any) {
            toast.error("Failed to refresh invitations", { description: error.message });
        } finally {
            set({ isInvitationsLoading: false });
        }
    },
    
    fetchUserPermissions: async () => {
        if (get().permissionsPromise) await get().permissionsPromise;

        const promise = (async () => {
            set({ isPermissionsLoading: true });
            try {
                const permissions = await api.getInterviewPermissions();
                set({ userPermissions: permissions });
            } catch (error) {
                // Fail silently, user might not be in an org
            } finally {
                set({ isPermissionsLoading: false, permissionsPromise: null });
            }
        })();
        set({ permissionsPromise: promise });
    },

    updateOrganization: async (data) => {
        set({ isUpdating: true });
        try {
            const updatedOrg = await api.updateMyOrganization(data);
            set({ organization: updatedOrg });
            toast.success("Organization updated successfully!");
            return true;
        } catch (error: any) {
            toast.error("Failed to update organization", { description: error.message });
            return false;
        } finally {
            set({ isUpdating: false });
        }
    },

    removeMember: async (member: OrganizationMember) => {
        try {
            await api.removeOrganizationMember(member.id);
            toast.success(`${member.full_name} has been removed.`);
            set({ membersPromise: null, orgPromise: null }); // Invalidate promises
            await Promise.all([get().fetchMembers(), get().fetchOrganization()]); // Refetch data
        } catch (error: any) {
            toast.error("Failed to remove member", { description: error.message });
        }
    },

    banMember: async (member: OrganizationMember, reason?: string) => {
        try {
            await api.banOrganizationMember(member.id, reason);
            toast.success(`${member.full_name} has been banned.`);
            set({ membersPromise: null }); // Invalidate members promise
            await get().fetchMembers(); // Refetch members list
        } catch (error: any) {
            toast.error(`Failed to ban ${member.full_name}`, { description: error.message });
        }
    },

    unbanMember: async (member: OrganizationMember) => {
        try {
            await api.unbanOrganizationMember(member.id);
            toast.success(`${member.full_name} has been unbanned.`);
            set({ membersPromise: null }); // Invalidate members promise
            await get().fetchMembers(); // Refetch members list
        } catch (error: any) {
            toast.error(`Failed to unban ${member.full_name}`, { description: error.message });
        }
    },
    
    clearOrganization: () => {
        set({
            organization: null,
            organizationStats: null,
            members: [],
            invitations: [],
            userPermissions: null,
            orgPromise: null,
            membersPromise: null,
            invitationsPromise: null,
        });
    },

    // --- ADDED LOGO ACTIONS START ---
    uploadLogo: async (file: File) => {
        set({ isUpdating: true });
        try {
            const updatedOrg = await api.uploadCompanyLogo(file);
            // Update the local organization state with the new data (including the new URL)
            set({ organization: updatedOrg });
        } catch (error: any) {
            throw error; // Propagate error for component to handle (e.g., show toast)
        } finally {
            set({ isUpdating: false });
        }
    },

    deleteLogo: async () => {
        set({ isUpdating: true });
        try {
            await api.deleteCompanyLogo();
            
            // Manually clear the logo URL in the local state
            set(state => ({ 
                organization: state.organization ? { ...state.organization, company_logo_url: null } : null
            }));

            // Optionally, call fetchOrganization to refresh the full data and stats
            await get().fetchOrganization(); 
            toast.success("Company logo deleted successfully!");
        } catch (error: any) {
            throw error; // Propagate error for component to handle
        } finally {
            set({ isUpdating: false });
        }
    },
}));